import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
// Removed axios and API_BASE_URL imports, will use the centralized 'api' instance
import api, { getChartSnapshotConfigs, requestChartSnapshot } from "../api/api"; // Adjusted path

const TIMEFRAME_CHOICES = ['1m', '3m', '5m', '15m', '30m', '1h', '2h', '4h', '1D', '1W', '1M'];
const DEFAULT_SNAPSHOT_TIMEFRAMES = ['5m', '15m', '1h', '4h', '1D'];

const JournalModal = ({ tradeId, onClose }) => {
  // Fields from your sample
  const [action, setAction] = useState("Opened Position");
  const [tradeTimeframe, setTradeTimeframe] = useState("1H");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [strategyTag, setStrategyTag] = useState("");
  const [emotionalState, setEmotionalState] = useState("");
  const [marketCondition, setMarketCondition] = useState("");

  // Multiple attachments to upload
  const [attachments, setAttachments] = useState([]);

  // New state variables for auto-snapshot
  const [chartConfigs, setChartConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [snapshotMessage, setSnapshotMessage] = useState(''); // User feedback for snapshot
  const [snapshotSymbol, setSnapshotSymbol] = useState(''); // For runtime symbol input
  const [selectedSnapshotTimeframes, setSelectedSnapshotTimeframes] = useState([...DEFAULT_SNAPSHOT_TIMEFRAMES]); // For runtime timeframes

  // Submission loading state
  const [submitting, setSubmitting] = useState(false);

  // useEffect to fetch chart configurations
  useEffect(() => {
    const fetchConfigs = async () => {
      setLoadingConfigs(true);
      setSnapshotMessage('');
      try {
        const configsData = await getChartSnapshotConfigs();
        // Assuming configsData is an array. If it can be null/undefined, handle appropriately.
        setChartConfigs(configsData && Array.isArray(configsData) ? configsData : []);
      } catch (error) {
        console.error("Failed to fetch chart configs:", error);
        setSnapshotMessage(`Error loading snapshot configs: ${error.message || 'Unknown error'}`);
        setChartConfigs([]); // Ensure it's an empty array on error
      } finally {
        setLoadingConfigs(false);
      }
    };
    if (tradeId) { // Only fetch if tradeId is present, or remove if not a dependency
        fetchConfigs();
    }
  }, [tradeId]); // Re-fetch if tradeId changes, or empty array [] if it should only run once

  const handleSubmit = async () => {
    // Basic validation
    if (!reason.trim()) {
      alert("Please enter a reason before submitting the journal.");
      return;
    }

    setSubmitting(true);
    setSnapshotMessage(''); // Reset snapshot message on new submission

    try {
      // 1) POST text fields to /trade_journal/journals/
      const journalPayload = {
        trade: tradeId,
        action,
        trade_timeframe: tradeTimeframe,
        reason,
        details,
        strategy_tag: strategyTag,
        emotional_state: emotionalState,
        market_condition: marketCondition,
      };

      // Refactored call to create journal entry using 'api' instance
      const newJournal = await api.post("/trade_journal/journals/", journalPayload);
      const journalId = newJournal.id;

      // Auto-snapshot logic
      if (selectedConfigId && journalId) {
        if (!snapshotSymbol.trim()) {
          setSnapshotMessage("Error: Symbol is required for automated snapshot.");
          // Potentially throw an error or prevent further submission steps if this is critical path
        } else if (selectedSnapshotTimeframes.length === 0) {
          setSnapshotMessage("Error: At least one timeframe must be selected for automated snapshot.");
        } else if (selectedSnapshotTimeframes.length > 5) {
          setSnapshotMessage("Error: No more than 5 timeframes can be selected for automated snapshot.");
        } else {
          try {
            setSnapshotMessage('Requesting auto snapshot(s)...');
            const snapshotPayload = {
              symbol: snapshotSymbol,
              timeframes: selectedSnapshotTimeframes,
              journal_entry_id: journalId,
            };
            const snapshotRes = await requestChartSnapshot(selectedConfigId, snapshotPayload);
            setSnapshotMessage(`Snapshot generation tasked (Task ID: ${snapshotRes.task_id || 'N/A'}). Check attachments shortly.`);
          } catch (snapError) {
            console.error("❌ Auto snapshot error:", snapError);
            const snapErrorMessage = snapError.response?.data?.detail || snapError.message || "Unknown snapshot error";
            setSnapshotMessage(`Auto snapshot failed: ${snapErrorMessage}. Please attach manually if needed.`);
          }
        }
      }

      // Manual attachments logic (refactored to use 'api' instance)
      if (attachments.length > 0 && journalId) {
        for (const file of attachments) {
          const formData = new FormData();
          formData.append("journal", journalId);
          formData.append("files", file); // Backend expects 'files' (plural) for getlist
        
          // Using 'api' instance for file upload
          // Note: Axios typically sets Content-Type for FormData automatically
          await api.post("/trade_journal/attachments/", formData);
        }
      }
      
      let alertMessage = "Journal entry submitted successfully ✅";
      // Append snapshot status to the main alert if a snapshot was attempted
      if (snapshotMessage) {
        if (snapshotMessage.toLowerCase().includes("failed") || snapshotMessage.toLowerCase().includes("error")) {
          alertMessage += `\nWarning: ${snapshotMessage}`;
        } else if (snapshotMessage.toLowerCase().includes("tasked")) {
          alertMessage += `\n${snapshotMessage}`;
        }
      }
      alert(alertMessage);
      onClose(); // Close the modal

    } catch (error) {
      console.error("❌ Journal submission error:", error);
      let errorMessage = error.response?.data?.detail || error.message || "Failed to submit journal entry.";
       // If a snapshot error occurred and wasn't part of the main error, append it.
      if (snapshotMessage && (snapshotMessage.toLowerCase().includes("failed") || snapshotMessage.toLowerCase().includes("error")) && !errorMessage.includes(snapshotMessage)) {
          errorMessage += `\n${snapshotMessage}`;
      }
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-textPrimary/50 backdrop-blur-sm flex justify-center items-center z-[200] p-4"> {/* Overlay with blur and padding for scroll; high z-index to float above grid */}
      <div className="bg-backgroundSurface rounded-lg p-6 w-full max-w-3xl relative shadow-2xl border border-borderDefault max-h-[90vh] overflow-y-auto"> {/* Modal container - increased max-w, added border, max-h and overflow */}
        <button
          className="absolute top-4 right-4 text-textSecondary hover:text-textPrimary text-xl z-10" /* Close button - adjusted position for padding */
          onClick={onClose}
        >
          ✖
        </button>
        <h2 className="text-xl font-semibold mb-6 text-primary text-center"> {/* Title - increased mb */}
          Journal This Trade
        </h2>

        {/* Flex container for two columns */}
        <div className="flex flex-col md:flex-row md:space-x-6">
          
          {/* Column 1 */}
          <div className="md:w-1/2 space-y-3"> {/* Reduced space-y */}

            {/* Action */}
            <div className="mb-3"> {/* Reduced mb */}
              <label className="block text-sm font-medium text-textSecondary mb-1"> {/* Applied new label style */}
                Action
              </label>
          <input
            type="text"
            className="w-full p-2 border border-borderDefault rounded-md bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-primary focus:border-primary" /* Input styles */
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="Opened Position / Closed Position"
          />
        </div>

        {/* Trade Timeframe */}
        <div className="mb-3"> {/* Reduced mb */}
          <label className="block text-sm font-medium text-textSecondary mb-1"> {/* Applied new label style */}
            Trade Timeframe
          </label>
          <input
            type="text"
            className="w-full p-2 border border-borderDefault rounded-md bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-primary focus:border-primary" /* Input styles */
            value={tradeTimeframe}
            onChange={(e) => setTradeTimeframe(e.target.value)}
            placeholder="1H, 4H, etc."
          />
        </div>

        {/* Reason */}
        <div className="mb-3"> {/* Reduced mb */}
          <label className="block text-sm font-medium text-textSecondary mb-1"> {/* Applied new label style */}
            Reason
          </label>
          <textarea
            className="w-full p-3 border border-borderDefault rounded-md bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-primary focus:border-primary" /* Textarea styles */
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why did you enter or exit this trade?"
          />
        </div>

        {/* Details */}
        <div className="mb-3"> {/* Reduced mb */}
          <label className="block text-sm font-medium text-textSecondary mb-1"> {/* Applied new label style */}
            Additional Details
          </label>
          <textarea
            className="w-full p-3 border border-borderDefault rounded-md bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-primary focus:border-primary" /* Textarea styles */
            rows={2}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Notes on how you managed the trade, scaling, etc."
          />
        </div>

        {/* Strategy Tag */}
        <div className="mb-3"> {/* Reduced mb */}
          <label className="block text-sm font-medium text-textSecondary mb-1"> {/* Applied new label style */}
            Strategy Tag
          </label>
          <input
            type="text"
            className="w-full p-2 border border-borderDefault rounded-md bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-primary focus:border-primary" /* Input styles */
            value={strategyTag}
            onChange={(e) => setStrategyTag(e.target.value)}
            placeholder="E.g. Breakout Strategy"
          />
        </div>

        {/* Emotional State */}
        <div className="mb-3"> {/* Reduced mb */}
          <label className="block text-sm font-medium text-textSecondary mb-1"> {/* Applied new label style */}
            Emotional State
          </label>
          <input
            type="text"
            className="w-full p-2 border border-borderDefault rounded-md bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-primary focus:border-primary" /* Input styles */
            value={emotionalState}
            onChange={(e) => setEmotionalState(e.target.value)}
            placeholder="Calm, Confident, Fearful, etc."
          />
        </div>

        {/* Market Condition */}
        <div className="mb-3"> {/* Reduced mb */}
          <label className="block text-sm font-medium text-textSecondary mb-1"> {/* Applied new label style */}
            Market Condition
          </label>
          <input
            type="text"
            className="w-full p-2 border border-borderDefault rounded-md bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-primary focus:border-primary" /* Input styles */
            value={marketCondition}
            onChange={(e) => setMarketCondition(e.target.value)}
            placeholder="Trending, Ranging, etc."
          />
        </div>

      </div> {/* End Column 1 */}

      {/* Column 2 */}
      <div className="md:w-1/2 space-y-3 mt-4 md:mt-0"> {/* Reduced space-y */}

        {/* Automated Chart Snapshot Section */}
        <div className="mb-3"> {/* Reduced mb */}
          <label className="block text-sm font-medium text-textSecondary mb-1"> {/* Applied new label style */}
            Automated Chart Snapshot (Optional)
          </label>
          {loadingConfigs && <p className="text-sm text-textSecondary">Loading configurations...</p>}
          {!loadingConfigs && chartConfigs.length === 0 && (
            <p className="text-sm text-textSecondary">
              No snapshot indicator templates found. You can attach files manually below.
            </p>
          )}
          {!loadingConfigs && chartConfigs.length > 0 && (
            <>
              <select
                value={selectedConfigId}
                onChange={(e) => {
                  setSelectedConfigId(e.target.value);
                  if (!e.target.value) { // Reset symbol and timeframes if "None" is selected
                    setSnapshotSymbol('');
                    setSelectedSnapshotTimeframes([...DEFAULT_SNAPSHOT_TIMEFRAMES]);
                  }
                }}
                className="w-full p-2 border border-borderDefault rounded-md bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-primary focus:border-primary mb-2"
              >
                <option value="">None - Attach Manually Below</option>
                {chartConfigs.map(config => (
                  <option key={config.id} value={config.id}>
                    {config.is_global ? "[Global] " : ""}{config.name || `Template ID: ${config.id}`}
                    {/* TODO: Could also add (Created by: {config.user_username}) if username is available and useful */}
                  </option>
                ))}
              </select>

              {selectedConfigId && (
                <div className="space-y-3 mt-2 p-3 border border-borderDefault rounded-md">
                  <div>
                    <label htmlFor="snapshotSymbol" className="block text-xs font-medium text-textSecondary mb-1"> {/* Applied new label style */}
                      Symbol for Snapshot (e.g., OANDA:EURUSD)
                    </label>
                    <input
                      type="text"
                      id="snapshotSymbol"
                      value={snapshotSymbol}
                      onChange={(e) => setSnapshotSymbol(e.target.value)}
                      className="w-full p-2 border border-borderDefault rounded-md bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-primary focus:border-primary"
                      placeholder="Enter symbol"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-textSecondary mb-1"> {/* Applied new label style */}
                      Timeframes for Snapshots (Select up to 5)
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {TIMEFRAME_CHOICES.map(tf => (
                        <label key={tf} className="flex items-center space-x-2 p-1 border border-borderDefault rounded-md hover:bg-backgroundAlt cursor-pointer">
                          <input
                            type="checkbox"
                            value={tf}
                            checked={selectedSnapshotTimeframes.includes(tf)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedSnapshotTimeframes(prev => {
                                if (checked) {
                                  return prev.length < 5 ? [...prev, tf] : prev;
                                } else {
                                  return prev.filter(item => item !== tf);
                                }
                              });
                            }}
                            className="form-checkbox h-4 w-4 text-primary rounded focus:ring-primary"
                            disabled={!selectedSnapshotTimeframes.includes(tf) && selectedSnapshotTimeframes.length >= 5}
                          />
                          <span className="text-xs text-textPrimary">{tf}</span>
                        </label>
                      ))}
                    </div>
                    {selectedSnapshotTimeframes.length >= 5 && <p className="text-xs text-yellow-500 mt-1">Maximum 5 timeframes selected.</p>}
                     {selectedSnapshotTimeframes.length === 0 && <p className="text-xs text-red-500 mt-1">Please select at least one timeframe.</p>}
                  </div>
                </div>
              )}
            </>
          )}
          {snapshotMessage && (
            <p className={`text-xs mt-2 ${snapshotMessage.toLowerCase().includes("failed") || snapshotMessage.toLowerCase().includes("error") ? 'text-red-500' : 'text-green-500'}`}>
              {snapshotMessage}
            </p>
          )}
        </div>
        
        {/* Manual Attachments Section */}
        <div className="mb-3"> {/* Reduced mb */}
          <label className="block text-sm font-medium text-textSecondary mb-1"> {/* Applied new label style */}
            Manual Attachments (Optional)
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            className="block w-full text-sm text-textSecondary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-secondary file:text-textPrimary hover:file:bg-secondary/80" /* File input styles */
            onChange={(e) => {
              // Convert FileList into an array
              setAttachments([...e.target.files]);
            }}
          />
          {attachments.length > 0 && (
            <p className="text-xs text-textSecondary mt-1">
              {attachments.length} file(s) selected
            </p>
          )}
        </div>

      </div> {/* End Column 2 */}
    </div> {/* End Flex container for columns */}

        {/* Submit Button - moved outside the two-column flex container */}
        <button
          className="btn btn-primary w-full mt-6" /* Increased mt */
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Submit Journal Entry"}
        </button>
      </div>
    </div>,
    document.body
  );
};

export default JournalModal;
